import {assert} from 'Asserts';
import {NON_STANDARD_AMINO_ACIDS, NonStandardAminoAcids, StandardAminoAcidSLA} from 'biojs/Codons';
import {ProteinAlignment} from 'constants/AlignmentConstants';
import {MolstarFileViewer} from 'constants/PreviewTypeConstants';
import {User} from 'constants/UserConstants';
import {Bioentity, BioentitySummary} from 'folder-items/FileConstants';
import {BioentityNote, ExternalFile} from 'notebook/util/NoteUtilConstants';

export type BasicAminoAcidSLA = Exclude<StandardAminoAcidSLA, '*'>;

// Keep in sync with STANDARD_AMINO_ACIDS on benchling/pybio/proteins/constants.py
const STANDARD_AMINO_ACIDS = [
  'A',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'K',
  'L',
  'M',
  'N',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'V',
  'W',
  'Y',
];

export type AllAminoAcidSLA = BasicAminoAcidSLA | NonStandardAminoAcids;
const ALL_AMINO_ACIDS = [...STANDARD_AMINO_ACIDS, ...NON_STANDARD_AMINO_ACIDS];

export type ProteinAlignmentSLA = AllAminoAcidSLA | '-';
const ALIGNMENT_AMINO_ACIDS = [...ALL_AMINO_ACIDS, '-'];

/*
  Slightly modified constants from biopython, to match results from expasy.
  These are the average isotopic masses (as opposed to the monoisotopic mass, see
  http://www.ionsource.com/tutorial/isotopes/slide8.htm).

  Molecular weights for B, Z are non-standard and were reverse engineered from
  http://web.expasy.org/protparam/. Molecular weights for J and X were taken from Geneious
*/
const AMINO_ACID_WEIGHTS_ROUNDED: {
  [aminoAcid in AllAminoAcidSLA]: number;
} = {
  A: 89.09,
  B: 132.65,
  C: 121.15,
  D: 133.1,
  E: 147.13,
  F: 165.19,
  G: 75.07,
  H: 155.16,
  I: 131.17,
  J: 131.2,
  K: 146.19,
  L: 131.17,
  M: 149.21,
  N: 132.12,
  O: 255.31,
  P: 115.13,
  Q: 146.15,
  R: 174.2,
  S: 105.09,
  T: 119.12,
  U: 168.06,
  V: 117.15,
  W: 204.23,
  X: 143.7,
  Y: 181.19,
  Z: 146.75,
};

/**
 * Standard AA weights are taken from Expasy's average isotopic masses with water added.
 * https://education.expasy.org/student_projects/isotopident/htdocs/aa-list.html
 *
 * O and U weights are taken from Geneious https://assets.geneious.com/manual/10.1/GeneiousManualsu52.html
 *
 * Weight for B is calculated as an average of N and D, Z as average of Q and E, J as average of I and L,
 * and X as average of all standard AAs.
 */
const AMINO_ACID_WEIGHTS_PRECISE: {
  [aminoAcid in AllAminoAcidSLA]: number;
} = {
  A: 89.09408,
  B: 132.61148,
  C: 121.15408,
  D: 133.10388,
  E: 147.13078,
  F: 165.19188,
  G: 75.06718,
  H: 155.15638,
  I: 131.17468,
  J: 131.17468,
  K: 146.18938,
  L: 131.17468,
  M: 149.20788,
  N: 132.11908,
  O: 237.3018,
  P: 115.13198,
  Q: 146.14598,
  R: 174.20278,
  S: 105.09348,
  T: 119.12038,
  U: 150.0388,
  V: 117.14788,
  W: 204.22848,
  X: 136.90131,
  Y: 181.19128,
  Z: 146.63838,
};

/**
 * Eventually, we want to use greater precision with molecular weight calculations for all customers
 * but have no immediate plans to roll this out, so AMINO_ACID_WEIGHTS_PRECISE is unused for now.
 */
// Keep in sync with benchling/pybio/proteins/constants.py
export function getAminoAcidWeights(): {
  [aminoAcid in AllAminoAcidSLA]: number;
} {
  return AMINO_ACID_WEIGHTS_ROUNDED;
}

export function getMolecularWeightPrecision(): number {
  return 2;
}
export const PRECISION_ISOELECTRIC_POINT = 2;
export const PRECISION_EXTINCTION_COEFFICIENT = 2;
export const PRECISION_ABSORBANCE = 3;
export const PRECISION_INSTABILITY_INDEX = 2;
export const INSTABILITY_INDEX_THRESHOLD = 40;

const ProteinConstants = {
  STANDARD_AMINO_ACIDS,
  ALL_AMINO_ACIDS,
  ALIGNMENT_AMINO_ACIDS,
  DEFAULT_START_INDEX: 1,
};

// Right now all amino acids have a weight so we can check `aa in AMINO_ACID_WEIGHTS` but this may change
// going forwards. This assert lets us know if anything changes.
assert(
  ALL_AMINO_ACIDS.every(
    // eslint-disable-next-line benchlint/no-in-operator
    (aminoAcid) => aminoAcid in AMINO_ACID_WEIGHTS_ROUNDED && aminoAcid in AMINO_ACID_WEIGHTS_PRECISE
  ) &&
    Object.keys(AMINO_ACID_WEIGHTS_ROUNDED).length === ALL_AMINO_ACIDS.length &&
    Object.keys(AMINO_ACID_WEIGHTS_PRECISE).length === ALL_AMINO_ACIDS.length,
  'Each amino acid should have a molecular weight.'
);

export default ProteinConstants;

export interface ProteinAlignmentSummary {
  id: string;
  name: string;
  createdAt: string;
  proteinId: string;
  modifiedAt?: string;
}

export interface ProteinAlignmentState {
  alignment: ProteinAlignment | ProteinAlignmentSummary;
  knownLinkedProteinIds: Set<string>;
}

export interface ProteinPart {
  id: number;
  // Start/end locations of annotations on a part are relative to the container sequence
  annotations: Array<ProteinAnnotationVersion>;
  start: number;
  end: number;
  proteinId: string;
}

export interface ProteinStructureExternalFile extends ExternalFile {
  previewType: MolstarFileViewer;
}

export interface ProteinStructure {
  externalFile: ProteinStructureExternalFile;
}

export interface ProteinAnnotationVersion {
  id: string;
  name: string;
  start: number;
  end: number;
  color: string;
  type: string;
  notes: string;
  // Optional for now while AA parts feature is behind a flag
  isFromPart?: boolean;
}

export interface ProteinVersion {
  aminoAcids: string;
  annotations: Array<ProteinAnnotationVersion>;
  createdAt: string;
  creator: User;
  id: number;
  modifiedAt: string;
  name: string;
  parentId: number | null;
  structures: Array<ProteinStructure>;
  // Optional for now while AA parts feature is behind a flag
  parts?: Array<ProteinPart>;
  // Optional because we only use this on the frontend currently in the Bulk Assembly concatenation flow.
  fragments?: Array<{
    start: number;
    end: number;
    color: string;
  }>;
}

export interface ProteinSummary extends BioentitySummary {
  projectFolderId?: string;
  version?: ProteinVersion | null;
  latestVersionId: number;
}

export interface Protein extends Bioentity {
  note: BioentityNote;
  version: ProteinVersion;
  isContainedInComplexPolymer: boolean;
  isLatest: boolean;
  latestVersionId: number;
  alignments: Array<ProteinAlignmentSummary>;
  linkedAlignments: Array<ProteinAlignmentSummary>;
  projectFolderId?: string;
  thirdPartyStorageServiceData: {
    lastSentTimestamp: string | null;
    lastSentUser: User | null;
  };
}

export const PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY = 'proteins.defaultDatabaseImport';
